import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { useMutation } from 'react-query';

import ProductComplaintPopup, { ProductComplaintPayload } from './ProductComplaintPopup';
import { createProductComplaint } from '@/api/products/createProductComplaint';
import AlertModal from '@/components/Common/Modal/AlertModal';
import { PRODUCT_DETAIL } from '@/const/common';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { SALES_TYPE_NORMAL } from '@/const/products';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import { memberAtom } from '@/store/member';

interface ProductComplaintProps {
  product?: ProductDetailResponse;
}

const ProductComplaint = ({ product }: ProductComplaintProps) => {
  const authRedirect = useAuthRedirect();
  const { showToast } = useToastContext();
  const member = useAtomValue(memberAtom);

  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [isOpenCompleteModal, setIsOpenCompleteModal] = useState(false);

  const productId = product?.id;
  const isDirectSale = product?.salesType?.code === SALES_TYPE_NORMAL;
  const isMyProduct = !!member?.id && product?.sellerId === member.id;

  const { mutate: complainProduct, isLoading } = useMutation(createProductComplaint, {
    onSuccess: (data) => {
      if (!data?.success) {
        showToast(COMMON_TOAST_ERROR, 'error');
        return;
      }

      setIsOpenPopup(false);
      setIsOpenCompleteModal(true);
    },
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'error');
    },
  });

  const handleClickComplaint = () => {
    if (!productId) {
      return;
    }

    if (!authRedirect(PRODUCT_DETAIL(productId), { feature: '신고하기' })) {
      return;
    }

    setIsOpenPopup(true);
  };

  const handleConfirm = ({ complaintType, productComplaintText }: ProductComplaintPayload) => {
    if (!productId) {
      return;
    }

    complainProduct({ productId, complaintType, productComplaintText });
  };

  // 신고는 내가 등록하지 않은 직거래 매물에만 노출한다.
  if (!isDirectSale || isMyProduct) {
    return null;
  }

  return (
    <>
      <div className="px-4 py-6">
        <div className="overflow-hidden rounded-xl bg-white shadow-[0_0_2px_rgba(0,0,0,0.08),0_6px_24px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-[18px] pb-2">
            <p className="font-bold text-gray-8 text-[13px] leading-[22px]">안전한 거래를 위한 안내</p>
            <p className="mt-1 break-keep text-gray-6 text-[11px] leading-[18px]">
              직트럭은 등록시스템만을 제공하며, 판매자가 직접 등록한 차량의 모든 책임은 판매자에게 있습니다.
            </p>
          </div>
          <div className="flex items-center justify-between gap-2 px-4 py-4 border-t border-secondaryRed-0 bg-secondaryRed-light">
            <p className="font-bold text-secondaryRed-1 text-[12px] leading-[18px]">문제가 있는 차량인가요?</p>
            <button
              type="button"
              onClick={handleClickComplaint}
              className="shrink-0 px-[11px] rounded-full bg-[#DB2C21] font-bold text-white text-[11px] leading-[18px]"
            >
              신고하기
            </button>
          </div>
        </div>
      </div>

      <ProductComplaintPopup
        isOpen={isOpenPopup}
        isSubmitting={isLoading}
        onClose={() => setIsOpenPopup(false)}
        onConfirm={handleConfirm}
      />

      <AlertModal
        isOpen={isOpenCompleteModal}
        content="해당 매물을 신고했어요"
        onClose={() => setIsOpenCompleteModal(false)}
      />
    </>
  );
};

export default ProductComplaint;
